/**
 * RulesAtRiskView Component
 *
 * Tab-based view for displaying compliance rules in the audit sidebar.
 * Shows "At Risk" rules (non-compliant and warnings) and "Compliant" rules
 * in separate tabs with AuditRuleCard components.
 *
 * Features staggered fade-in animations when rules are added to the list.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import { useLazyGetRuleQuery } from '@features/rules/store/api';
import { CheckCircle as CheckIcon, KeyboardArrowDown as ArrowDownIcon } from '@mui/icons-material';
import { Box, MenuItem, Select, type SelectChangeEvent, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useAppSelector } from '@store/hooks/core';
import { useModal } from '@store/hooks/useModal';
import { AnimatePresence, motion } from 'motion/react';

import type { CptAnalysisData } from '../../hooks/useAuditIssues';
import { useReauditReconciliation } from '../../hooks/useReauditReconciliation';
import { selectCardStates } from '../../store/selectors';
import type { BillingSupport, RuleComplianceAssessment } from '../../types';
import { selectFindingRows } from '../../utils/coverageRows';
import type { InterruptHandlers } from '../cards';

import { AuditRuleCardContainer } from './AuditRuleCardContainer';
import { CptCardContainer } from './CptCardContainer';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RulesAtRiskViewProps {
  rules: RuleComplianceAssessment[];
  noteId: string;
  encounterId?: string;
  onFixWithAI: (reason: string) => void;
  onScrollToSection?: (sectionId: string) => void;
  billingSupport?: BillingSupport | null;
  cptAnalysis?: CptAnalysisData | null;
  onUseSuggestedCodes?: (codes: string[]) => void;
  interruptHandlers?: InterruptHandlers;
}

type TabValue = 'at-risk' | 'compliant';

// ---------------------------------------------------------------------------
// Animation variants
// ---------------------------------------------------------------------------

const ruleCardVariants = {
  hidden: {
    opacity: 0,
    y: -16,
    scale: 0.97,
  },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: index * 0.04,
      duration: 0.25,
      ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number],
    },
  }),
  exit: {
    opacity: 0,
    scale: 0.97,
    transition: {
      duration: 0.15,
    },
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const RulesAtRiskView: React.FC<RulesAtRiskViewProps> = ({
  rules,
  noteId,
  encounterId,
  onFixWithAI,
  onScrollToSection,
  billingSupport,
  cptAnalysis,
  onUseSuggestedCodes,
  interruptHandlers,
}) => {
  const [activeTab, setActiveTab] = useState<TabValue>('at-risk');
  const { noteAudit } = useEventTracking();
  const { open: openModal } = useModal();
  const [fetchRule] = useLazyGetRuleQuery();
  const cardStates = useAppSelector(selectCardStates);

  // Revisions written before the producer fix carry coverage rows that report
  // policy execution rather than this note; they never reach the view.
  const findingRules = useMemo(() => selectFindingRows(rules), [rules]);

  // Reconcile card states when audit data refreshes (reaudit)
  useReauditReconciliation(findingRules, cardStates);

  // Track which rules have been seen (for animation purposes)
  const seenRuleIdsRef = useRef<Set<string>>(new Set());

  // Group rules
  const atRiskRules = findingRules.filter(
    r => r.compliant === 'no' || r.compliant === 'warning',
  );
  const compliantRules = findingRules.filter(r => r.compliant === 'yes');

  // Detect new rules for staggered animation
  const newRuleIdsThisRender = useMemo(() => {
    const newIds: string[] = [];
    findingRules.forEach(rule => {
      if (!seenRuleIdsRef.current.has(rule.rule_id)) {
        newIds.push(rule.rule_id);
      }
    });
    return newIds;
  }, [findingRules]);

  useEffect(() => {
    if (newRuleIdsThisRender.length > 0) {
      newRuleIdsThisRender.forEach(id => seenRuleIdsRef.current.add(id));
    }
  }, [newRuleIdsThisRender]);

  // Sort: graduated cards → bottom, then non-compliant before warning.
  // Non-graduated completed/ignored cards stay in place for review/undo.
  const sortedAtRiskRules = useMemo(() => {
    return [...atRiskRules].sort((a, b) => {
      const aState = cardStates[a.rule_id];
      const bState = cardStates[b.rule_id];
      const aStatus = aState?.status ?? 'idle';
      const bStatus = bState?.status ?? 'idle';
      const aIsDone = (aStatus === 'completed' || aStatus === 'ignored') && !!aState?.graduated;
      const bIsDone = (bStatus === 'completed' || bStatus === 'ignored') && !!bState?.graduated;

      if (aIsDone !== bIsDone) return aIsDone ? 1 : -1;

      if (a.compliant !== b.compliant) {
        return a.compliant === 'no' ? -1 : 1;
      }

      return 0;
    });
  }, [atRiskRules, cardStates]);

  // Counts for dynamic header
  const nonCompliantCount = findingRules.filter(r => r.compliant === 'no').length;
  const warningCount = findingRules.filter(r => r.compliant === 'warning').length;

  const handleFilterChange = (event: SelectChangeEvent<TabValue>) => {
    const newValue = event.target.value as TabValue;
    noteAudit.tabSwitched({
      note_id: noteId,
      encounter_id: encounterId,
      previous_tab: activeTab,
      new_tab: newValue,
      at_risk_count: atRiskRules.length,
      compliant_count: compliantRules.length,
    });
    setActiveTab(newValue);
  };

  const getAnimationIndex = (ruleId: string): number => {
    const indexInNewRules = newRuleIdsThisRender.indexOf(ruleId);
    if (indexInNewRules === -1) return -1;
    return indexInNewRules;
  };

  const shouldAnimateRule = (ruleId: string): boolean => {
    return newRuleIdsThisRender.includes(ruleId);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Dynamic header + dropdown */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.25 }}>
          {nonCompliantCount > 0
            ? `JotAudit flagged ${nonCompliantCount} issue${nonCompliantCount !== 1 ? 's' : ''}`
            : warningCount > 0
              ? `JotAudit found ${warningCount} suggestion${warningCount !== 1 ? 's' : ''}`
              : 'All rules compliant'}
        </Typography>
        <Typography variant="caption" sx={{ color: 'textColors.lightText', display: 'block', lineHeight: 1.4, mb: 1.5 }}>
          {nonCompliantCount > 0
            ? 'Update your note to prevent claim rejection'
            : warningCount > 0
              ? 'Review suggestions to improve documentation'
              : 'Your note meets all compliance requirements'}
        </Typography>
        <Select
          value={activeTab}
          onChange={handleFilterChange}
          size="small"
          IconComponent={ArrowDownIcon}
          sx={{
            minWidth: 160,
            fontSize: '0.8rem',
            fontWeight: 500,
            borderRadius: 1.5,
            '& .MuiSelect-select': { py: 0.75, px: 1.5 },
            bgcolor: 'background.paper',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'borderColors.lightprimary' },
          }}
        >
          <MenuItem value="at-risk">At Risk ({atRiskRules.length})</MenuItem>
          <MenuItem value="compliant">Compliant ({compliantRules.length})</MenuItem>
        </Select>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2, pt: 1 }}>

        {/* CPT mismatch card — top of At Risk tab */}
        {activeTab === 'at-risk' && billingSupport && cptAnalysis && onUseSuggestedCodes && (
          <Box sx={{ mb: 1 }}>
            <CptCardContainer
              billingSupport={billingSupport}
              cptAnalysis={cptAnalysis}
              noteId={noteId}
              encounterId={encounterId}
              onUseSuggestedCodes={onUseSuggestedCodes}
            />
          </Box>
        )}

        {activeTab === 'at-risk' ? (
          sortedAtRiskRules.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <AnimatePresence mode="popLayout">
                {sortedAtRiskRules.map(rule => {
                  const isNewRule = shouldAnimateRule(rule.rule_id);
                  const animationIndex = getAnimationIndex(rule.rule_id);

                  return (
                    <motion.div
                      key={rule.rule_id}
                      variants={ruleCardVariants}
                      initial={isNewRule ? 'hidden' : false}
                      animate="visible"
                      exit="exit"
                      custom={isNewRule ? animationIndex : 0}
                      layout="position"
                      layoutId={rule.rule_id}
                    >
                      <AuditRuleCardContainer
                        assessment={rule}
                        noteId={noteId}
                        encounterId={encounterId}
                        onFixWithAI={onFixWithAI}
                        onScrollToSection={onScrollToSection}
                        expandMode="modal"
                        interruptHandlers={interruptHandlers}
                      />
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                All rules compliant
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This note meets all applicable rules
              </Typography>
            </Box>
          )
        ) : compliantRules.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {compliantRules.map(rule => (
              <Box
                key={rule.rule_id}
                onClick={async () => {
                  const result = await fetchRule(rule.rule_id);
                  if (result.data) {
                    openModal(MODAL_TYPES.RULE_DETAIL, {
                      rule: result.data,
                      isAdmin: false,
                      assessment: rule,
                    });
                  }
                }}
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'borderColors.lightprimary',
                  bgcolor: 'background.paper',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'borderColors.brand',
                    boxShadow: 1,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                  <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {rule.rule_title}
                  </Typography>
                </Box>
                {rule.suggestion && (
                  <Typography variant="caption" sx={{ color: 'textColors.lightText', pl: 3 }}>
                    {rule.suggestion}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No compliant rules to display
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RulesAtRiskView;
